import type { AnnotationTerm, ConvertedMetadata, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type { DataFieldAbstractTypes, LineItem } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { UIAnnotationTypes, UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { Definition, DefinitionOrBoolean } from 'typescript-json-schema';
import type { SectionTypeV2 } from '../../v2/types';
import type { PresentationVariantV4 } from '../../v4/types';
import {
    DEFINITION_LINK_PREFIX,
    createAnnotationPath,
    prepareRef,
    getDefinitionKey,
    replaceNamespaces,
    addSectionTitle,
    addSectionDescription
} from '..';
import type {
    FacetConfig,
    FacetSection,
    SectionType,
    SchemaDefinition,
    SchemaDefinitionKey,
    OneOfOption,
    MessageParameters
} from '@sap/ux-specification-types';
import { DefinitionName, SchemaTag, SchemaKeyName, UIVOCABULARY } from '@sap/ux-specification-types';
import { getStableIdPartFromValue } from '../../v4/utils/StableIdHelper';
import i18next from 'i18next';
import { dataFieldStrategyContext } from '../DataFieldStrategy';

interface PropertyInDefinition {
    definition?: string;
    property?: string;
    schema?: Definition;
}

interface EnumDefinitionParsingOptions {
    excludeKey?: string;
    resolveDefinition?: boolean;
    // Read enum key from schema keys by passed key property
    keyProperties?: SchemaKeyName[];
    // If key property resolution fails, then use description/label as fallback solution for enum key
    descriptionAsFallback?: boolean;
}

/**
 * Adds enum values to the app schema. Either as 'enum' -  if multiple entries exist,
 * or as 'const', if it is only a single value.
 *
 * @param enumEntries - array of strings for setting up the enum definition
 * @param currentDefinition - currentDefinition in the schema
 * @param multiSelect - indication for multi selection with comma separate string approach
 */
export function addEnumToSchema(enumEntries: string[], currentDefinition: SchemaDefinition, multiSelect = false) {
    if (!currentDefinition) {
        return;
    }
    if (enumEntries.length > 0) {
        if (enumEntries.length > 1) {
            currentDefinition.enum = enumEntries;
        } else {
            currentDefinition.const = enumEntries[0];
        }
    }
    if (multiSelect) {
        currentDefinition.multiSelect = multiSelect;
    }
}

/**
 * Determines the existing UI annotations of the given list (validAnnotations).
 *
 * @param entityType - The entity type containing annotations to be processed
 * @param validAnnotations - List of valid UI annotation terms to be processed
 * @returns the list of valid annotations
 */
export function getListOfValidAnnotations(entityType: EntityType, validAnnotations: UIAnnotationTerms[]): string[] {
    const enumEntries: string[] = [];
    const uiAnnotations = entityType?.annotations?.UI;
    if (uiAnnotations) {
        for (const key in uiAnnotations) {
            const annotation = uiAnnotations[key];
            if (validAnnotations.indexOf(annotation.term) > -1) {
                const enumEntry = annotation.qualifier
                    ? `${annotation.term}#${annotation.qualifier}`
                    : `${annotation.term}`;
                if (enumEntries.indexOf(enumEntry) === -1) {
                    enumEntries.push(enumEntry);
                }
            }
        }
    }
    return enumEntries;
}

/**
 * Adds an enum filled with existing UI annotations of the given list (validAnnotations).
 *
 * @param {EntityType} entityType - The entity type containing annotations to be processed
 * @param {UIAnnotationTerms[]} validAnnotations - List of valid UI annotation terms to be processed
 * @param {Definition} annoPath - the annotation path property in the app schema where the enum shall be added
 */
export function addEnumForValidAnnotations(
    entityType: EntityType,
    validAnnotations: UIAnnotationTerms[],
    annoPath: Definition
): void {
    const enumEntries = getListOfValidAnnotations(entityType, validAnnotations);
    if (enumEntries.length > 0) {
        addEnumToSchema(enumEntries, annoPath);
    }
}

/**
 * Adds an enum filled with existing UI annotations for single-tab variants of a table.
 *
 * @param {EntityType} entityType - The entity type containing annotations to be processed
 * @param {Definition} appSchema - app specific JSON schema
 * @param definitionName - name of the definition in the app schema
 */
export function addEnumForSingleTabVariant(
    entityType: EntityType,
    appSchema: Definition,
    definitionName: string
): void {
    const annoPath = (appSchema.definitions[definitionName] as Definition).properties[SchemaTag.annotationPath];
    const validAnnotations = [UIAnnotationTerms.SelectionVariant, UIAnnotationTerms.SelectionPresentationVariant];
    addEnumForValidAnnotations(entityType, validAnnotations, annoPath as Definition);
}

/**
 * Adds an enum filled with existing UI annotations for multi-tab variants of a table.
 *
 * @param appSchema - app specific JSOn schema
 * @param definitionName - name of the definition in the app schema
 * @param {EntitySet} entitySet - current entity set of the page or view
 */
export function addEnumForMultiTabVariant(appSchema: Definition, definitionName: string, entitySet: EntitySet): void {
    const annoPath = (appSchema.definitions[definitionName] as Definition)?.properties.annotationPath;
    const validAnnotations = [UIAnnotationTerms.SelectionVariant, UIAnnotationTerms.SelectionPresentationVariant];
    addEnumForValidAnnotations(entitySet.entityType, validAnnotations, annoPath as Definition);
}

/**
 * Adds the enum values for entitySet as part of the multiple view scenario.
 *
 * @param {ConvertedMetadata} oDataServiceAVT - combined service metadata, as returned by annotation vocabularies tools
 * @param appSchema - app specific JSOn schema
 * @param definitionName - name of the definition in the app schema
 */
export function addEnumForVariantEntitySet(
    oDataServiceAVT: ConvertedMetadata,
    appSchema: Definition,
    definitionName: string
): void {
    const entitySet = (appSchema.definitions[definitionName] as Definition).properties.entitySet as Definition;
    const enumEntries = [];

    oDataServiceAVT.entitySets.forEach((es: EntitySet) => {
        enumEntries.push(es.name);
    });
    addEnumToSchema(enumEntries, entitySet);
}

/**
 * Adds an enum filled with existing UI FieldGroup annotations for annotationPath property in MultiEdit.
 *
 * @param entityType - The entity type containing annotations to be processed
 * @param appSchema - app specific JSOn schema
 * @param definitionName - name of the definition in the app schema
 */
export function addEnumFieldGroupAnnotationPath(
    entityType: EntityType,
    appSchema: Definition,
    definitionName: string
): void {
    const annoPath = (appSchema.definitions[definitionName] as Definition).properties.annotationPath as Definition;
    const validAnnotations = [UIAnnotationTerms.FieldGroup];
    addEnumForValidAnnotations(entityType, validAnnotations, annoPath);
}

/**
 * Method adds definition for 'RelatedFacetKeys' as enum with description and adds references to 'RelatedFacetKeys' for custom section definitions.
 *
 * @param appSchema App specific schema that potentially gets enhanced
 * @param sectionDefinitions array of section definitions which should be updated with new reference to 'RelatedFacetKeys' enum.
 * @param facetSections Array of facet section objects which is used to generate enum values.
 * @param idProperties Array of property names which should be looked up in 'FacetSection' object.
 *  Please note that method depends on order and takes first non empty value associated to passed properties.
 */
export function addDefinitionForRelatedFacetKeys(
    appSchema: Definition,
    sectionDefinitions: string[],
    facetSections: FacetSection[],
    idProperties: Array<keyof Omit<FacetSection, 'custom'>>
): void {
    const oneOfSections = facetSections.map((section: FacetSection) => {
        // Loop acceptable properties in received order and find first entry with value
        const propertyName = idProperties.find((property) => !!section[property]);
        const schemaIdForSection = section[propertyName];
        const label = section.label || section.title;
        return {
            const: schemaIdForSection,
            ...(label && { description: label }),
            ...(section.custom && { custom: section.custom })
        };
    });
    // Change 'relatedFacet' only when we have entries for sections - otherwise use generic.
    if (oneOfSections.length) {
        // Create new definition in schema as 'oneOf'.
        // Simpler way would be to use 'enum', but then there no option to pass description.
        appSchema.definitions[DefinitionName.RelatedFacetKeys] = {
            type: 'string',
            oneOf: oneOfSections
        } as Definition;

        // Add enum definition
        for (const name of sectionDefinitions) {
            const definition = appSchema.definitions[name] as Definition;
            const property = definition.properties['relatedFacet'];
            definition.properties['relatedFacet'] = {
                anyOf: [
                    {
                        ...(typeof property === 'object' ? property : undefined),
                        $ref: `${DEFINITION_LINK_PREFIX}${DefinitionName.RelatedFacetKeys}`
                    },
                    property
                ]
            };
        }
    }
}

/**
 * Receives a facet key and returns the key as used in schema.
 *
 * @param facetKey - key of the facet, as used in annotation
 * @returns facetKeyInSchema - key of the facet, as used in schema
 */
export function getFacetKeyInSchema(facetKey: string): string {
    return facetKey.replace('#', '::');
}

/**
 * Returns the name of a facet and definition, as key in definition.
 *
 * @param definition name of definition, as used in schema
 * @param facetKey - key of the facet, as used in annotation
 * @returns definition key of facet, as used in definition
 */
export function getFacetDefinitionKey(definition: string, facetKey: string): string {
    return `${definition}<${getFacetKeyInSchema(facetKey)}>`;
}

/**
 * Adds an existing schema definition to a new definition in the app schema.
 *
 * @param definition name of definition, as used in schema
 * @param facetKey - key of the facet, as used in annotation
 * @param {Definition} appSchema App schema in general
 * @param {boolean} useDefinitionAsKey If set to false, facet key will solely be used as key, else encapsulated within definition key `definition<facetKey>`
 * @returns {Definition} part of app schema that has been added and filled
 */
export function parseSchemaDefinition(
    definition: string,
    facetKey: string,
    appSchema: Definition,
    useDefinitionAsKey = true
): Definition {
    let newDefinitionKey = facetKey;
    if (useDefinitionAsKey) {
        newDefinitionKey = getFacetDefinitionKey(definition, facetKey);
    }
    if (definition && definition !== newDefinitionKey) {
        try {
            const schemaDefinition = JSON.parse(JSON.stringify(appSchema.definitions[definition])) as Definition;
            appSchema.definitions[newDefinitionKey] = schemaDefinition;
        } catch {
            throw new Error(i18next.t('INCONSISTENTSCHEMA', { function: 'parseSchemaDefinition', definition }));
        }
    }
    return appSchema.definitions[newDefinitionKey] as Definition;
}

/**
 * Method receives custom extension base definition name and detect if it should be combined with lineItemId.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param lineItemId - line item ID, as comprise in stable ID
 * @param customExtensionDefinitionName - name of the custom extension definition
 * @returns $ref path for definition.
 */
export function getCustomExtensionDefinitionName(
    appSchema: Definition,
    lineItemId: string,
    customExtensionDefinitionName: string
): string {
    let definitionName: string;
    if (
        lineItemId === DefinitionName.LineItems ||
        lineItemId === 'LineItemsOfSPV::LineItem' ||
        lineItemId === `@${UIAnnotationTerms.LineItem}`
    ) {
        definitionName = `${DEFINITION_LINK_PREFIX}${customExtensionDefinitionName}`;
    } else {
        definitionName = `${DEFINITION_LINK_PREFIX}${customExtensionDefinitionName}<${lineItemId}>`;
        parseSchemaDefinition(customExtensionDefinitionName, lineItemId, appSchema, true);
    }
    return definitionName;
}

/**
 * Functions used both by V2 and V4 to add the common definitions for a line item to the app schema.
 *
 * @param isObjectPage - indicates that the page is an object page
 * @param appSchema - the app specific schema that shall get enhanced
 * @param lineItemAnnotation - the UI.LineItem annotation, comprising all records
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 * @param lineItemId - line item ID, as comprise in stable ID
 * @param customColumnDefinitionName - (optional) definition name of custom column.
 *        Passed in the V4 case, then additional properties of this type will be allowed.
 * @param customActionDefinitionName - (optional) definition name of custom action.
 * @returns the actions definition plus the annotation path to the given line item
 */
export function addCommonLineItemDefinitions(
    isObjectPage: boolean,
    appSchema: Definition,
    lineItemAnnotation: AnnotationTerm<LineItem>,
    entityType: EntityType,
    lineItemId: string,
    customColumnDefinitionName?: string,
    customActionDefinitionName?: string
): { actions: Definition; footerActions: Definition; lineItemPath: string } {
    let lineItemPath: string;

    appSchema.definitions[lineItemId] = {
        type: 'object',
        properties: {},
        description: 'Columns'
    };
    appSchema.definitions[lineItemId][SchemaTag.isViewNode] = true;
    appSchema.definitions[lineItemId].additionalProperties = false;
    if (customColumnDefinitionName) {
        const customColumnRef: string = getCustomExtensionDefinitionName(
            appSchema,
            lineItemId,
            customColumnDefinitionName
        );
        appSchema.definitions[lineItemId].additionalProperties = { $ref: customColumnRef };
    }
    // Copy toolbar
    const toolBar = isObjectPage ? DefinitionName.ObjectPageToolBar : DefinitionName.ToolBar;
    const schemaIdForToolBar = `${toolBar}<${lineItemId}>`;
    try {
        appSchema.definitions[schemaIdForToolBar] = JSON.parse(JSON.stringify(appSchema.definitions[`${toolBar}`]));
    } catch {
        throw new Error(
            i18next.t('INCONSISTENTSCHEMA', { function: 'addCommonLineItemDefinitions', definition: `${toolBar}` })
        );
    }

    const schemaIdForActions = `${DefinitionName.Actions}<${prepareRef(lineItemId)}>`;
    (appSchema.definitions[schemaIdForToolBar] as Definition).properties.actions['$ref'] =
        DEFINITION_LINK_PREFIX + schemaIdForActions;

    const actionId = lineItemId.startsWith(DefinitionName.LineItems)
        ? DefinitionName.Actions
        : DefinitionName.ObjectPageToolBarActions;
    let actions: SchemaDefinition | undefined;
    try {
        actions = appSchema.definitions[schemaIdForActions] = JSON.parse(
            JSON.stringify(appSchema.definitions[actionId])
        );
        actions.properties = {};
        // Handle actions additional properties - add custom action reference if it is supported
        actions.additionalProperties = false;
        if (customActionDefinitionName) {
            const customActionRef = getCustomExtensionDefinitionName(appSchema, lineItemId, customActionDefinitionName);
            actions.additionalProperties = { $ref: customActionRef };
        }
    } catch {
        throw new Error(
            i18next.t('INCONSISTENTSCHEMA', {
                function: 'addCommonLineItemDefinitions',
                definition: `${actionId}`
            })
        );
    }

    // Copy LR footer
    let footerActions, schemaIdForFooter;
    if (lineItemId === DefinitionName.LineItems && appSchema.properties['footer']) {
        schemaIdForFooter = `${DefinitionName.Footer}<${lineItemId}>`;
        (appSchema.properties['footer'] as Definition).$ref = `${DEFINITION_LINK_PREFIX}${schemaIdForFooter}`;
        try {
            appSchema.definitions[schemaIdForFooter] = JSON.parse(
                JSON.stringify(appSchema.definitions[DefinitionName.GenericFooter])
            );
        } catch {
            throw new Error(
                i18next.t('INCONSISTENTSCHEMA', {
                    function: 'addCommonLineItemDefinitions',
                    definition: DefinitionName.GenericFooter
                })
            );
        }

        const schemaIdForFooterActions = `${DefinitionName.FooterActions}<${prepareRef(lineItemId)}>`;
        (appSchema.definitions[schemaIdForFooter] as Definition).properties.actions['$ref'] =
            DEFINITION_LINK_PREFIX + schemaIdForFooterActions;

        footerActions = appSchema.definitions[schemaIdForFooterActions] = JSON.parse(
            JSON.stringify(appSchema.definitions[DefinitionName.Actions])
        );
        footerActions.properties = {};
        footerActions.additionalProperties = false;
    }
    //Determine annotation path
    if (lineItemAnnotation) {
        lineItemPath = createAnnotationPath(
            entityType?.fullyQualifiedName,
            lineItemAnnotation.term,
            lineItemAnnotation.qualifier
        );
        if (lineItemPath) {
            appSchema.definitions[schemaIdForToolBar][SchemaTag.annotationPath] = lineItemPath;
            if (footerActions) {
                appSchema.definitions[schemaIdForFooter][SchemaTag.annotationPath] = lineItemPath;
            }
        }
    }

    return { actions, footerActions, lineItemPath };
}

export enum DataType {
    String = 'String',
    Rating = 'Rating',
    Progress = 'Progress',
    DataPoint = 'DataPoint',
    Contact = 'Contact',
    Address = 'Address',
    Chart = 'Chart',
    FieldGroup = 'FieldGroup',
    IntentBasedNavigation = 'IntentBasedNavigation'
}

/**
 * Common logic to build the dataType tag from an annotation type.
 *
 * @param {string} annotation - the annotation type
 * @returns the dataType as string
 */
export function getDataTypeFromAnnotation(annotation: string): string {
    const dataType = annotation.substring(annotation.lastIndexOf('.') + 1);
    return dataType.replace(/Type$/, '');
}

/**
 * Determines the dataType of the target that shall be added to a property in schema.
 *
 * @param target - the given target
 * @returns - the dataType as string
 */
export function determineDataTypeOfTarget(target: any): string | undefined {
    let dataType: string;

    if (target?.Value) {
        if (target.Visualization === 'UI.VisualizationType/Rating') {
            dataType = DataType.Rating;
        } else if (target.Visualization === 'UI.VisualizationType/Progress') {
            dataType = DataType.Progress;
        } else if (target.$Type === 'com.sap.vocabularies.UI.v1.DataPointType') {
            dataType = DataType.DataPoint;
        } else {
            dataType = target.Value?.$target?.type.replace('Edm.', '');
        }
    } else if (target?.$Type?.startsWith('com.sap.vocabularies.')) {
        dataType = getDataTypeFromAnnotation(target.$Type);
    }
    return dataType;
}

/**
 * Determines the dataType that shall be added to a property in schema for any kind of field like:
 * - field of a field group
 * - line item record
 * - selection field
 * passed as object.
 *
 * @param field - the given field
 * @returns - the dataType as string
 */
export function determineDataType(field: object): string | undefined {
    let dataType: string;
    try {
        if (field['Value']) {
            if (field['Value'].$target?.type) {
                dataType = field['Value'].$target.type.replace('Edm.', '');
            } else {
                dataType = DataType.String;
            }
        } else if (field['Target']) {
            dataType = determineDataTypeOfTarget(field['Target']?.$target);
        } else if (field['$target']) {
            dataType = field['$target'].type.replace('Edm.', '');
        } else if (field['$Type']?.startsWith('com.sap.vocabularies.')) {
            dataType = getDataTypeFromAnnotation(field['$Type']);
        } else if (field['type']) {
            dataType = field['type'];
        }
        return dataType;
    } catch {
        return dataType;
    }
}

/**
 * Adds the dataType to the given schema field definition, if the data type cane be determined.
 *
 * @param definition - given schema definition
 * @param field - given field definition = annotation property, at AVT
 */
export function addDataTypeToDefinition(definition: object, field: object): void {
    const dataType = determineDataType(field);
    if (dataType) {
        definition[SchemaTag.dataType] = dataType;
    }
}

/**
 * Creates a definition link for a facet and definition name.
 *
 * @param {DefinitionName} definition - name of definition, as used in schema
 * @param {string} facetKey - key of the facet, as used in annotation
 * @returns definition link of facet, as used in definition
 */
export function getFacetDefinitionLink(definition: DefinitionName | SectionTypeV2, facetKey: string): string {
    return `${DEFINITION_LINK_PREFIX}${getFacetDefinitionKey(definition, facetKey)}`;
}

/**
 * Generates a unique facet definition link by appending an index to the facet key if necessary.
 *
 * @param definition The definition name or section type used to derive the facet definition link.
 * @param facetKey The base key of the facet to generate the definition link.
 * @param appSchema The application schema containing existing definitions where the uniqueness will be validated.
 * @param lineItemPath The path to the line item to check if the facet definition is associated with it.
 * @returns A string representing a unique facet definition link.
 */
export function getUniqueFacetDefinitionLink(
    definition: DefinitionName | SectionTypeV2,
    facetKey: string,
    appSchema: Definition,
    lineItemPath: string
): string {
    let index = 0;
    let isFacetDefinitionKeyUniqOrReusable = false;
    let facetDefinitionLink = '';
    do {
        // The definition link at index 0 serves as the default one
        const indexedFacetKey = `${facetKey}${index === 0 ? '' : '.' + index}`;
        facetDefinitionLink = getFacetDefinitionLink(definition, indexedFacetKey);
        index++;
        // Check if the facet definition already exists in the application schema
        // If the duplicated element is placed in the same line item, we can reuse the definition
        const facetDefinition = appSchema.definitions[getFacetDefinitionKey(definition, indexedFacetKey)] as Definition;
        const facetLineItemPath = (facetDefinition?.[`annotationPath`] ?? '').split('/').slice(0, -1).join('/');
        isFacetDefinitionKeyUniqOrReusable = !facetDefinition || facetLineItemPath === lineItemPath;
    } while (!isFacetDefinitionKeyUniqOrReusable);
    return facetDefinitionLink;
}

/**
 * Creates and adds a reference link to a definition.
 *
 * @param {Definition} schemaPart - part of schema to add $ref to
 * @param {string} schemaRef - reference to be added
 * @param {string} schemaKey - key to be encapsulated within reference as `schemaRef<schemaKey>`
 */
export function addDefinitionRef(schemaPart: Definition, schemaRef: string, schemaKey?: string): void {
    const schemaLink = schemaKey ? `${schemaRef}<${schemaKey}>` : schemaRef;
    schemaPart.$ref = `${DEFINITION_LINK_PREFIX}${schemaLink}`;
}

/**
 * Extracts annotationPath from facet or Chart/LineItem fullyQualifiedName.
 *
 * @param facet facet or Chart/LineItem
 * @returns {string} annotationPath
 */
export function getFacetAnnotationPath(facet: FacetConfig | PresentationVariantV4 | object): string | undefined {
    let result;
    if ((facet as FacetConfig).annotationPath) {
        result = (facet as FacetConfig).annotationPath;
    } else if ((facet as PresentationVariantV4).fullyQualifiedName) {
        const [namespace, target] = (facet as PresentationVariantV4).fullyQualifiedName.split(`@${UIVOCABULARY}`);
        result = `/${namespace}/@${UIVOCABULARY}${target}`;
    }
    return result;
}

/**
 * Adds definitions for Address and Contact items in object page sections to the app schema.
 *
 * @param facet - current facet.
 * @param facetKey - facet key.
 * @param sections - schema of current sections definition.
 * @param appSchema - app specific schema that potentially gets enhanced.
 * @param sectionPrefix - prefix to distinguish Section and HeaderSection
 * @param subSectionPrefix - prefix to distinguish subsections within the schema
 * @param versionPostfix - optional postfix to distinguish schema versions
 */
export function handleAddressContact(
    facet: FacetConfig,
    facetKey: string,
    sections: Definition,
    appSchema: Definition,
    sectionPrefix: SectionType,
    subSectionPrefix?: SectionType,
    versionPostfix = ''
) {
    const definitionName = `ObjectPage${sectionPrefix}${facet.base}${versionPostfix}` as DefinitionName;
    const subDefinitionName = `ObjectPage${subSectionPrefix}${facet.base}${versionPostfix}` as DefinitionName;
    sections.properties[facetKey] = {
        $ref: getFacetDefinitionLink(subSectionPrefix ? subDefinitionName : definitionName, facetKey)
    };
    const definition = parseSchemaDefinition(
        subSectionPrefix ? subDefinitionName : definitionName,
        facetKey,
        appSchema,
        true
    );
    addSectionTitle(facet, definition, facetKey);
    addSectionDescription(facet, definition, facetKey);
    definition[SchemaTag.annotationPath] = facet.annotationPath;
    definition[SchemaTag.isViewNode] = true;
}

/**
 * Method updates schema definition's property.
 *
 * @param {DefinitionOrBoolean} definition Definition object.
 * @param {string} key Property name.
 * @param {unknown} value New value.
 */
function updateSchemaProperty(definition: DefinitionOrBoolean, key: string, value: unknown): void {
    if (typeof definition === 'object') {
        definition[key] = value;
    }
}

/**
 * Add schema definitions for fields of createWithParameterDialog.
 *
 * @param {object} appSchema - Application-specific schema, to be enhanced
 * @param {EntityType} entityType - Given entity type for which entries shall get generated
 * @param {string=} suffix - floorplan specific suffix (optional)
 */
export function addFieldsType(appSchema: Definition, entityType: EntityType, suffix = prepareRef('')): void {
    const itemsEnum = entityType.entityProperties.map((property) => property.name);
    const fields4Dialog = appSchema.definitions[`${DefinitionName.Fields4Dialog}${suffix}`] as Definition;
    updateSchemaProperty(fields4Dialog.properties.fields['items'], 'enum', itemsEnum);
}

/**
 * Method finds first schema definition key by passed key names.
 *
 * @param keys Schema definitions keys to search in.
 * @param keyProperties Key properties to use as query.
 * @returns Found first schema definition key.
 */
function findFirstSchemaDefinitionKey(
    keys?: SchemaDefinitionKey[],
    keyProperties?: SchemaKeyName[]
): SchemaDefinitionKey | undefined {
    if (!keyProperties || !keys) {
        return;
    }
    for (const keyProperty of keyProperties) {
        const schemaDefinitionKey = keys.find((innerKey: SchemaDefinitionKey) => innerKey.name === keyProperty);
        if (schemaDefinitionKey) {
            return schemaDefinitionKey;
        }
    }
}

/**
 * Method returns enum entry for passed schema definition.
 *
 * @param {Definition} appSchema App specific JSON schema
 * @param {SchemaDefinition} definition Target definition to resolve description.
 * @param {string} key Default enum key.
 * @param {EnumDefinitionParsingOptions} [options] Additional parsing options.
 * @returns {string | undefined} Enum entry of passed definition.
 */
export function getEnumEntry(
    appSchema: Definition,
    definition: SchemaDefinition,
    key: string,
    options?: EnumDefinitionParsingOptions
): { const: string; description: string | undefined; custom?: boolean } {
    let description = definition.description;
    let keys = definition.keys;
    let custom = !definition.annotationPath;
    if (definition.$ref) {
        // Try to read description from reference
        const definitionKey = getDefinitionKey(definition.$ref);
        const refDefinition: SchemaDefinition | boolean = appSchema.definitions?.[definitionKey];
        if (typeof refDefinition === 'object') {
            custom = !refDefinition.annotationPath;
            if (options?.resolveDefinition && !description) {
                description = refDefinition.description;
                keys = refDefinition.keys;
            }
        }
    }
    // Resolve enum key
    let enumKey = key;
    // Read key from schema keys by passed property
    const schemaDefinitionKey: SchemaDefinitionKey | undefined = findFirstSchemaDefinitionKey(
        keys,
        options?.keyProperties
    );
    if (schemaDefinitionKey) {
        enumKey = schemaDefinitionKey.value;
    } else if (options?.descriptionAsFallback && description) {
        // Fallback to use description/label as key. It is fallback solution for some extensions.
        enumKey = description;
    }

    return {
        const: enumKey,
        ...(description && { description }),
        ...(custom && { custom })
    };
}

/**
 * Method adds enum with key and description to schema definition.
 * Description can be easily used by UI to describe enum value/key.
 *
 * @param {Definition} appSchema App specific JSON schema
 * @param {DefinitionOrBoolean} origin Origin schema object containing key and description.
 * @param {PropertyInDefinition} target Target to update with enum.
 * @param {EnumDefinitionParsingOptions} [options] Additional parsing options.
 * @param {boolean} [options.resolveDefinition] Should logic use fallback to look into reference definition.
 */
export function addDescriptiveEnumDefinition(
    appSchema: Definition,
    origin: DefinitionOrBoolean,
    target: PropertyInDefinition,
    options?: EnumDefinitionParsingOptions
): void {
    if (typeof origin !== 'object') {
        // Safe checks
        return;
    }
    const oneOfEnum: Array<OneOfOption> = [];
    for (const key in origin.properties) {
        if (options?.excludeKey === key) {
            continue;
        }
        const property = origin.properties[key] as SchemaDefinition;
        const entry = getEnumEntry(appSchema, property, key, options);

        oneOfEnum.push(entry);
    }
    const definition = appSchema.definitions[target.definition] || target.schema;
    if (typeof definition === 'object') {
        if (target.property) {
            const property = definition.properties?.[target.property];
            updateSchemaProperty(property, 'oneOf', oneOfEnum);
        } else {
            updateSchemaProperty(definition, 'oneOf', oneOfEnum);
        }
    }
}

/**
 * Extracts the key of an action that shall be added to schema as SchemaTag.keys.
 *
 * @param action - full-blown action ID
 * @param isAction - indicates an action
 * @returns action ID stripped down to the last key part
 */
export function getActionKey(action: string, isAction: boolean): { key: string; ns: string } {
    let actionKey = action;
    let namespace;
    if (actionKey.indexOf('(') > 0) {
        actionKey = actionKey.substring(0, actionKey.indexOf('('));
    }
    if (isAction && actionKey.indexOf('.') > 0) {
        const firstSlash = actionKey.indexOf('/');
        const lastDot = actionKey.lastIndexOf('.');
        namespace = firstSlash > 0 ? actionKey.substring(0, firstSlash) : actionKey.substring(0, lastDot);
        actionKey = firstSlash > 0 ? actionKey.substring(firstSlash + 1) : actionKey.substring(lastDot + 1);
    }
    if (!namespace) {
        namespace = action.substring(0, action.lastIndexOf('.'));
    }
    return { key: actionKey.replace(/@/g, ''), ns: namespace };
}

/**
 * Adds the schema tag key to a line item record or property in schema.
 *
 * @param lineItemRecord - the given line item record
 * @param properties - list of all properties in schema
 * @param schemaKey - key of the given property
 */
export function addKeyToDefinition(lineItemRecord: object, properties: object, schemaKey: string): void {
    try {
        if (lineItemRecord['Value']) {
            properties[schemaKey][SchemaTag.keys] = [
                { name: SchemaKeyName.value, value: getStableIdPartFromValue(lineItemRecord['Value']) }
            ];
        } else if (
            lineItemRecord['$Type'] === UIAnnotationTypes.DataFieldWithAction ||
            lineItemRecord['$Type'] === UIAnnotationTypes.DataFieldForAction
        ) {
            const { key, ns } = getActionKey(lineItemRecord['Action'], true);
            properties[schemaKey][SchemaTag.keys] = [{ name: SchemaKeyName.action, value: key }];
            if (ns) {
                properties[schemaKey][SchemaTag.target] = ns;
            }
        } else if (lineItemRecord['$Type'] === UIAnnotationTypes.DataFieldForAnnotation) {
            const { key } = getActionKey(lineItemRecord['Target'].value, false);
            properties[schemaKey][SchemaTag.keys] = [{ name: SchemaKeyName.target, value: replaceNamespaces(key) }];
            properties[schemaKey][SchemaTag.target] =
                lineItemRecord['Target']?.$target?.fullyQualifiedName?.split('@')[0];
        } else if (
            lineItemRecord['$Type'] === UIAnnotationTypes.DataFieldForIntentBasedNavigation ||
            lineItemRecord['$Type'] === UIAnnotationTypes.DataFieldWithIntentBasedNavigation
        ) {
            const actionId =
                typeof lineItemRecord['Action'] === 'string' ? lineItemRecord['Action'] : lineItemRecord['Action'].path;
            const { key, ns } = getActionKey(actionId, true);
            properties[schemaKey][SchemaTag.keys] = [
                { name: `SemanticObject`, value: lineItemRecord[SchemaKeyName.semanticObject] },
                { name: `Action`, value: key }
            ];
            if (ns) {
                properties[schemaKey][SchemaTag.target] = ns;
            }
        }
    } catch {
        return;
    }
}

/**
 * Determines the description of a data field, e.g. for the column header.
 *
 * @param dataFieldAbstract - the given record of the line item annotation
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 * @returns the description of the data field
 */
export function getDataFieldDescription(dataFieldAbstract: DataFieldAbstractTypes, entityType: EntityType): string {
    try {
        const strategy = dataFieldStrategyContext.getStrategy(dataFieldAbstract.$Type);
        return strategy.generateDescription(dataFieldAbstract, entityType);
    } catch {
        return '';
    }
}

/**
 * Adds a message to the `messages` field of a schema element.
 *
 * @param {Definition} element - The schema definition to which the message should be added.
 * @param {MessageParameters} parameters - The message parameters including text and deletable flag.
 */
export function addMessageToSchema(element: Definition, { text, deletable = false }: MessageParameters): void {
    if (!element[SchemaTag.messages]) {
        element[SchemaTag.messages] = [];
    }

    if (Array.isArray(element[SchemaTag.messages])) {
        element[SchemaTag.messages].push({
            text,
            deletable
        });
    }
}
